import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { apiClient, handleResponse, API_BASE_URL } from '../api';
import i18n from '../i18n';
import { useOnboardingStore } from '../store/useOnboardingStore';

interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  theme_preference?: 'system' | 'light' | 'dark';
  preferred_language?: 'auto' | 'en' | 'ru';
  feature_flags?: Record<string, unknown>;
  new_ui?: boolean;
  onboarding_completed?: boolean;
  // Add other user properties as needed
}

interface RegistrationResponse {
  user_id: number;
  email: string;
  message: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
  ) => Promise<RegistrationResponse>;
  logout: () => void;
  isAuthenticated: () => boolean;
  refreshToken: () => Promise<string | null>;
}

const applyUserLanguage = (language?: string) => {
  if (!language || language === 'auto') {
    return;
  }
  if (i18n.language !== language) {
    i18n.changeLanguage(language).catch(error => {
      console.error('Failed to apply user language preference:', error);
    });
  }
};

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(
    localStorage.getItem('is_logged_in') === 'true' ? 'authenticated' : null,
  );

  const refreshToken = useCallback(async (): Promise<string | null> => {
    try {
      const response = await fetch(`${API_BASE_URL}/v2/refresh`, {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error('Refresh failed');

      const data = await response.json();
      setToken(data.access_token || 'authenticated');
      localStorage.setItem('is_logged_in', 'true');
      return data.access_token || 'authenticated';
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      return null;
    }
  }, []);

  const loadUser = useCallback(async () => {
    if (localStorage.getItem('is_logged_in') !== 'true') {
      setUser(null);
      return;
    }
    try {
      const userData = (await apiClient.get(`/v2/users/me`)) as User;
      setUser(userData);
      setToken('authenticated');
      applyUserLanguage(userData.preferred_language);
      if (userData.feature_flags) {
        localStorage.setItem('feature_flags', JSON.stringify(userData.feature_flags));
      }
      // Sync onboarding state with backend
      if (userData.onboarding_completed) {
        const { complete } = useOnboardingStore.getState();
        complete();
      }
    } catch (error) {
      console.error('Failed to load user data:', error);
      setUser(null);
      setToken(null);
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('is_logged_in');
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const isAuthenticated = () => {
    return !!user;
  };

  const login = async (email: string, password: string): Promise<void> => {
    // Create URLSearchParams for x-www-form-urlencoded format
    const formParams = new URLSearchParams();
    formParams.append('username', email);
    formParams.append('password', password);

    const response = await fetch(`${API_BASE_URL}/v2/login`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formParams,
    });

    const data = await handleResponse(response);
    setToken(data.access_token || 'authenticated');
    localStorage.setItem('is_logged_in', 'true');
    await loadUser();
  };

  const register = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
  ): Promise<RegistrationResponse> => {
    const response = await apiClient.post('/v2/register', {
      email,
      password,
      first_name: firstName,
      last_name: lastName,
    });
    return response as RegistrationResponse;
  };

  const logout = useCallback(() => {
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('feature_flags');
    localStorage.removeItem('is_logged_in');
    setUser(null);
  }, []);

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    logout,
    isAuthenticated,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
